import React, { useState } from 'react';
import styled from 'styled-components';
import { useNotification } from '@kardume/hooks';
import { PageHeader, Card, CardHeader, CardBody, Button, Select, Switch } from '@kardume/ui';

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 0;
  border-bottom: 1px solid ${(p) => p.theme.border};

  &:last-of-type {
    border-bottom: none;
  }
`;

const FieldInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  span {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  flex-wrap: wrap;
`;

const LocalNote = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
  max-width: 480px;
`;

const LANGUAGE_OPTIONS = [{ value: 'pt-BR', label: 'Português (Brasil)' }];

/** Local UI preferences kept in component state (no backend endpoint). */
interface Preferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  weeklyDigest: boolean;
  language: string;
}

const DEFAULT_PREFERENCES: Preferences = {
  emailNotifications: true,
  pushNotifications: false,
  weeklyDigest: true,
  language: 'pt-BR',
};

const SettingsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [preferences, setPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [saving, setSaving] = useState(false);

  const update = <K extends keyof Preferences>(key: K, value: Preferences[K]) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    // These preferences live only in the browser session for now - there is no
    // backend endpoint yet, so we simply acknowledge the local change.
    setSaving(true);
    setPreferences((prev) => ({ ...prev }));
    setSaving(false);
    showNotification('Preferências salvas com sucesso.', 'success');
  };

  return (
    <>
      <PageHeader
        title="Configurações"
        subtitle="Ajuste suas preferências de notificações e idioma."
      />

      <Card>
        <CardHeader>
          <h3>Notificações</h3>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <FieldInfo>
              <strong>Notificações por e-mail</strong>
              <span>Receba resumos e alertas importantes no seu e-mail.</span>
            </FieldInfo>
            <Switch
              checked={preferences.emailNotifications}
              onChange={(v) => update('emailNotifications', v)}
            />
          </FormGroup>

          <FormGroup>
            <FieldInfo>
              <strong>Notificações push</strong>
              <span>Alertas em tempo real no navegador.</span>
            </FieldInfo>
            <Switch
              checked={preferences.pushNotifications}
              onChange={(v) => update('pushNotifications', v)}
            />
          </FormGroup>

          <FormGroup>
            <FieldInfo>
              <strong>Resumo semanal</strong>
              <span>Um panorama do desempenho da sua loja toda semana.</span>
            </FieldInfo>
            <Switch
              checked={preferences.weeklyDigest}
              onChange={(v) => update('weeklyDigest', v)}
            />
          </FormGroup>

          <FormGroup>
            <FieldInfo>
              <strong>Idioma</strong>
              <span>Idioma da interface do painel.</span>
            </FieldInfo>
            <Select
              options={LANGUAGE_OPTIONS}
              value={preferences.language}
              onChange={(e) => update('language', e.target.value)}
              containerStyle={{ minWidth: 220 }}
            />
          </FormGroup>

          <Footer>
            <LocalNote>
              Estas preferências são salvas apenas neste dispositivo e ainda não são
              sincronizadas com sua conta.
            </LocalNote>
            <Button variant="primary" onClick={handleSave} isLoading={saving}>
              Salvar preferências
            </Button>
          </Footer>
        </CardBody>
      </Card>
    </>
  );
};

export default SettingsPage;
